'use client';

import { MaskedText } from '@/components/ui/MaskedText';

interface DonutDataItem {
  label: string;
  value: number;
  color: string;
  textColor?: string;
}

interface DonutChartProps {
  data: DonutDataItem[];
  total?: number;
  size?: number;
  isBlurRequired?: boolean;
}

export function DonutChart({ data, total, size = 267, isBlurRequired = false }: DonutChartProps) {
  const calculatedTotal = total || data.reduce((acc, item) => acc + item.value, 0);
  const radius = size / 2;
  const innerRadius = radius * 0.6;
  const center = size / 2;

  const calculatePath = (
    cx: number,
    cy: number,
    radius: number,
    startAngle: number,
    endAngle: number,
  ) => {
    const start = polarToCartesian(cx, cy, radius, endAngle);
    const end = polarToCartesian(cx, cy, radius, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';
    const d = [
      'M',
      start.x,
      start.y,
      'A',
      radius,
      radius,
      0,
      largeArcFlag,
      0,
      end.x,
      end.y,
      'L',
      cx,
      cy,
      'Z',
    ].join(' ');
    return d;
  };

  const polarToCartesian = (cx: number, cy: number, radius: number, angleInDegrees: number) => {
    const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
    return {
      x: cx + radius * Math.cos(angleInRadians),
      y: cy + radius * Math.sin(angleInRadians),
    };
  };

  const calculateTextPosition = (
    cx: number,
    cy: number,
    radius: number,
    startAngle: number,
    endAngle: number,
  ) => {
    const angle = (startAngle + endAngle) / 2;
    const position = polarToCartesian(cx, cy, radius * 0.8, angle);
    return position;
  };

  // Calculate the start and end angles for each segment
  let cumulativeValue = 0;
  const segments = data.map(item => {
    const startAngle = (cumulativeValue / calculatedTotal) * 360;
    cumulativeValue += item.value;
    const endAngle = (cumulativeValue / calculatedTotal) * 360;
    return {
      ...item,
      startAngle,
      endAngle,
    };
  });

  return (
    <div className="flex items-center justify-center md:mx-0 mx-auto">
      <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
        <div className="flex justify-center px-8">
          <svg width={size} height={size}>
            {segments.map((segment, index) => {
              let pathData: string;
              if (segment.startAngle === 0 && segment.endAngle === 360) {
                const firstHalf = calculatePath(center, center, radius, 0, 359.9);
                pathData = `${firstHalf}`;
              } else {
                pathData = calculatePath(
                  center,
                  center,
                  radius,
                  segment.startAngle,
                  segment.endAngle,
                );
              }

              const textPosition = calculateTextPosition(
                center,
                center,
                radius,
                segment.startAngle,
                segment.endAngle,
              );

              return (
                segment.value > 0 && (
                  <g key={index}>
                    <path d={pathData} fill={segment.color} stroke="#ffffff" strokeWidth="2" />
                    {isBlurRequired ? (
                      <rect
                        x={textPosition.x - 12}
                        y={textPosition.y - 8}
                        width="20"
                        height="16"
                        fill="#ffffff"
                        rx="2"
                      />
                    ) : (
                      <text
                        x={textPosition.x}
                        y={textPosition.y}
                        textAnchor="middle"
                        dy=".3em"
                        fontSize="14"
                        fill={segment.textColor ?? '#FFF'}
                        className="text-body-2 text-inverted"
                      >
                        {segment.value}
                      </text>
                    )}
                  </g>
                )
              );
            })}
            <circle cx={center} cy={center} r={innerRadius} fill="#ffffff" />
            {isBlurRequired ? (
              <>
                <rect
                  x={center - 10}
                  y={center - 20}
                  width="20"
                  height="16"
                  fill="#64748B"
                  rx="2"
                />
              </>
            ) : (
              <>
                <text
                  x={center}
                  y={center - 35}
                  textAnchor="middle"
                  dy="1.5em"
                  fontSize="14"
                  fill="#061938"
                  className="text-primary text-lg font-semibold"
                >
                  {calculatedTotal}
                </text>
              </>
            )}
            <text
              x="50%"
              y="55%"
              textAnchor="middle"
              dy="0.1em"
              fontSize="14"
              fill="#061938"
              className="text-primary text-body-2"
            >
              Violations
            </text>
          </svg>
        </div>
        <div className="pt-6 sm:pt-0">
          {segments.map((segment, index) => (
            <div key={index} className="flex items-center gap-3 mb-2">
              <span
                className="w-2 h-2 rounded-full shrink-0"
                style={{ backgroundColor: segment.color }}
              />
              {isBlurRequired ? (
                <MaskedText className="h-4 md:w-40 w-36 flex-1 !mb-0" />
              ) : (
                <span className="text-body-2 text-primary">{segment.label}</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
